import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Select from "react-select";
import { Controller, useForm } from "react-hook-form";
import Swal from "sweetalert2";

import CustomTable from "../../../components/CustomTable";
import { filterList } from "../../../libs/utils";
import { getAllPartnersList } from "../../../services/partnerConfigurations/partnerConfigurations";
import {
  createPartnerRateContract,
  getPartnerRateContracts,
  updatePartnerRateContract,
} from "../../../services/partnerRateContractsService";

const MAX_ARBITRAGE_PERCENT = 100;
const MIN_VOLUME_DAILY_LIMIT = 2500;
const MIN_VOLUME_FIVE_DAY_LIMIT = 10000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const EMPTY_FORM_VALUES = {
  partner: "",
  arbitrage_percent: "",
  discrepancy_bps: "",
  min_volume: "",
  max_volume: "",
  batch_volume: "",
  start_time: "",
  end_time: "",
  status: "ACTIVE",
  override_reason: "",
};

const STATUS_OPTIONS = [
  { label: "ACTIVE", value: "ACTIVE" },
  { label: "INACTIVE", value: "INACTIVE" },
];

const isTruthyStatus = (value) =>
  value === true ||
  String(value).toLowerCase() === "true" ||
  String(value).toLowerCase() === "yes" ||
  String(value).toUpperCase() === "ACTIVE";

const getPartnerValue = (item) => {
  if (typeof item === "string") {
    return item;
  }

  return (
    item?.partner ||
    item?.partnerName ||
    item?.partner_name ||
    item?.name ||
    item?.value ||
    ""
  );
};

const buildPartnerOptions = (partnerList = []) =>
  partnerList
    .filter((partner) => {
      return typeof partner === "object" && isTruthyStatus(partner?.active);
    })
    .map((partner) => getPartnerValue(partner))
    .filter(Boolean)
    .filter((value, index, list) => list.indexOf(value) === index)
    .sort()
    .map((value) => ({ label: value, value }));

const getListFromResponse = (response) => {
  const possibleList =
    response?.partner_rate_contracts ||
    response?.partnerRateContracts ||
    response?.data?.partner_rate_contracts ||
    response?.response?.partner_rate_contracts ||
    response?.data ||
    response?.response ||
    response;

  return Array.isArray(possibleList) ? possibleList : [];
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return Number(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(Number(value));
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const padDatePart = (value) => String(value).padStart(2, "0");

const toDateTimeLocalValue = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(Number(value));
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getFullYear()}-${padDatePart(
    date.getMonth() + 1,
  )}-${padDatePart(date.getDate())}T${padDatePart(
    date.getHours(),
  )}:${padDatePart(date.getMinutes())}`;
};

const getEpochMs = (value) => {
  if (!value) {
    return NaN;
  }

  return new Date(value).getTime();
};

const getDurationDays = (startTime, endTime) => {
  const duration = endTime - startTime;
  if (!Number.isFinite(duration) || duration < 0) {
    return 0;
  }

  return Math.max(1, Math.ceil(duration / MS_PER_DAY));
};

const getApiErrorMessage = (response, fallback) =>
  response?.error?.message || response?.message || fallback;

const FieldError = ({ error }) =>
  error?.message ? <span className="text-danger">{error.message}</span> : null;

function OfframpRateContracts() {
  const [contracts, setContracts] = useState([]);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [partnerFilter, setPartnerFilter] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError: setFormError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: EMPTY_FORM_VALUES,
  });

  const selectedPartner = partnerFilter?.value;

  const selectedPartnerOption = (value) =>
    partnerOptions.find((option) => option.value === value) ||
    (value ? { label: value, value } : null);

  const selectedStatusOption = (value) =>
    STATUS_OPTIONS.find((option) => option.value === value) ||
    (value ? { label: value, value } : null);

  const fetchPartners = useCallback(async () => {
    setPartnerLoading(true);
    const response = await getAllPartnersList();
    setPartnerLoading(false);

    if (!response?.success && !Array.isArray(response)) {
      return;
    }

    const partnerList = response?.response || response?.data || response || [];
    setPartnerOptions(buildPartnerOptions(partnerList));
  }, []);

  const fetchContracts = useCallback(async (partner) => {
    setLoading(true);
    setError("");

    const response = await getPartnerRateContracts(partner);
    setLoading(false);

    if (response?.success) {
      setContracts(getListFromResponse(response));
      return;
    }

    setContracts([]);
    const errorMessage = getApiErrorMessage(
      response,
      "Failed to load partner rate contracts",
    );
    setError(errorMessage);
    Swal.fire({
      title: "Warning",
      text: errorMessage,
      icon: "warning",
      confirmButtonText: "Ok",
    });
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  useEffect(() => {
    fetchContracts(selectedPartner);
  }, [fetchContracts, selectedPartner]);

  const validateBusinessRules = (formData) => {
    clearErrors();

    const startTime = getEpochMs(formData.start_time);
    const endTime = getEpochMs(formData.end_time);
    if (!Number.isFinite(startTime)) {
      setFormError("start_time", {
        type: "validate",
        message: "Start time is required",
      });
      return false;
    }

    if (!Number.isFinite(endTime)) {
      setFormError("end_time", {
        type: "validate",
        message: "End time is required",
      });
      return false;
    }

    if (endTime < startTime) {
      setFormError("start_time", {
        type: "validate",
        message: "Start time cannot be greater than end time",
      });
      setFormError("end_time", {
        type: "validate",
        message: "End time cannot be less than start time",
      });
      return false;
    }

    const minVolume = Number(formData.min_volume);
    const maxVolume = Number(formData.max_volume);

    if (maxVolume < minVolume) {
      setFormError("min_volume", {
        type: "validate",
        message: "Min volume cannot be greater than max volume",
      });
      setFormError("max_volume", {
        type: "validate",
        message: "Max volume cannot be less than min volume",
      });
      return false;
    }

    // const durationDays = getDurationDays(startTime, endTime);
    // const minVolumeLimit = Math.min(
    //   durationDays * MIN_VOLUME_DAILY_LIMIT,
    //   Math.ceil(durationDays / 5) * MIN_VOLUME_FIVE_DAY_LIMIT,
    // );

    // if (minVolume > minVolumeLimit) {
    //   setFormError("min_volume", {
    //     type: "validate",
    //     message: `Min volume cannot exceed ${formatCurrency(
    //       minVolumeLimit,
    //     )} for the selected duration`,
    //   });
    //   return false;
    // }

    // const batchVolumeLimit = Math.min(MAX_BATCH_VOLUME, maxVolume / durationDays);
    // if (batchVolume > batchVolumeLimit) {
    //   setFormError("batch_volume", {
    //     type: "validate",
    //     message: `Batch volume cannot exceed ${formatCurrency(
    //       batchVolumeLimit
    //     )} for the selected duration`,
    //   });
    //   return false;
    // }
    if (overrideRequired && !formData.override_reason?.trim()) {
      setFormError("override_reason", {
        type: "validate",
        message: "Override reason is required for overlapping active contracts",
      });
      return false;
    }

    return true;
  };

  const buildPayload = (formData) => {
    const payload = {
      partner: formData.partner,
      arbitrage_percent: Number(formData.arbitrage_percent),
      discrepancy_bps: Number(formData.discrepancy_bps),
      min_volume: Number(formData.min_volume),
      max_volume: Number(formData.max_volume),
      batch_volume: Number(formData.batch_volume),
      start_time: getEpochMs(formData.start_time),
      end_time: getEpochMs(formData.end_time),
      status: editingContract ? formData.status || "ACTIVE" : "ACTIVE",
      override_reason: editingContract
        ? formData.override_reason?.trim() || null
        : null,
    };

    if (editingContract?.id) {
      payload.id = Number(editingContract.id);
    }

    return payload;
  };

  const showSuccessMessage = (response, defaultMessage) => {
    const warnings = Array.isArray(response?.warnings) ? response.warnings : [];

    Swal.fire({
      title: warnings.length ? "Saved with warnings" : "Success",
      text: warnings.length ? warnings.join("\n") : defaultMessage,
      icon: warnings.length ? "warning" : "success",
      confirmButtonText: "Ok",
    });
  };

  const updateContractStatus = async (contract, status) => {
    if ((contract.status || "ACTIVE") === status) {
      return;
    }

    const confirmation = await Swal.fire({
      title: `Mark contract as ${status}?`,
      text:
        status === "INACTIVE"
          ? "This row will disappear from the list after save because the list shows active contracts only."
          : "This will update the contract status.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Update",
      cancelButtonText: "Cancel",
    });

    if (!confirmation.isConfirmed) {
      fetchContracts(selectedPartner);
      return;
    }

    const payload = {
      id: Number(contract.id),
      partner: contract.partner,
      arbitrage_percent: Number(contract.arbitrage_percent),
      discrepancy_bps: Number(contract.discrepancy_bps),
      min_volume: Number(contract.min_volume),
      max_volume: Number(contract.max_volume),
      batch_volume: Number(contract.batch_volume),
      start_time: Number(contract.start_time),
      end_time: Number(contract.end_time),
      status,
      override_reason: null,
    };

    setLoading(true);
    const response = await updatePartnerRateContract(payload);
    setLoading(false);

    if (response?.success) {
      fetchContracts(selectedPartner);
      showSuccessMessage(
        response,
        "Partner rate contract updated successfully",
      );
      return;
    }

    Swal.fire({
      title: "Oops",
      text: getApiErrorMessage(
        response,
        "Failed to update partner rate contract status",
      ),
      icon: "warning",
      confirmButtonText: "Ok",
    }).then(() => {
      fetchContracts(selectedPartner);
    });
  };

  const handleSaveContract = async (formData) => {
    if (!validateBusinessRules(formData)) {
      return;
    }

    if (editingContract) {
      const confirmation = await Swal.fire({
        title: "Confirm update?",
        text: "This will update the selected partner rate contract.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Update",
        cancelButtonText: "Cancel",
      });

      if (!confirmation.isConfirmed) {
        return;
      }
    }

    const payload = buildPayload(formData);

    setSubmitLoading(true);
    const response = editingContract
      ? await updatePartnerRateContract(payload)
      : await createPartnerRateContract(payload);
    setSubmitLoading(false);

    if (response?.success) {
      setDialogOpen(false);
      setEditingContract(null);
      fetchContracts(selectedPartner);
      showSuccessMessage(
        response,
        editingContract
          ? "Partner rate contract updated successfully"
          : "Partner rate contract created successfully",
      );
      return;
    }

    Swal.fire({
      title: "Oops",
      text: getApiErrorMessage(
        response,
        "Failed to save partner rate contract",
      ),
      icon: "warning",
      confirmButtonText: "Ok",
    });
  };

  const openCreateDialog = () => {
    setEditingContract(null);
    reset(EMPTY_FORM_VALUES);
    setDialogOpen(true);
  };

  const openEditDialog = (contract) => {
    setEditingContract(contract);
    reset({
      partner: contract.partner || "",
      arbitrage_percent: contract.arbitrage_percent ?? "",
      discrepancy_bps: contract.discrepancy_bps ?? "",
      min_volume: contract.min_volume ?? "",
      max_volume: contract.max_volume ?? "",
      batch_volume: contract.batch_volume ?? "",
      start_time: toDateTimeLocalValue(contract.start_time),
      end_time: toDateTimeLocalValue(contract.end_time),
      status: contract.status || "ACTIVE",
      override_reason: "",
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingContract(null);
    reset(EMPTY_FORM_VALUES);
  };

  const columns = [
    { id: "partner", label: "Partner", minWidth: 120, enableSort: true },
    {
      id: "arbitrage_percent",
      label: "Arbitrage %",
      minWidth: 50,
      align: "right",
      enableSort: true,
    },
    {
      id: "discrepancy_bps",
      label: "Discrepancy BPS",
      minWidth: 50,
      align: "right",
      enableSort: true,
    },
    {
      id: "min_volume",
      label: "Min Volume",
      minWidth: 130,
      align: "right",
      enableSort: true,
    },
    {
      id: "max_volume",
      label: "Max Volume",
      minWidth: 130,
      align: "right",
      enableSort: true,
    },
    {
      id: "batch_volume",
      label: "Batch Volume",
      minWidth: 130,
      align: "right",
      enableSort: true,
    },
    { id: "start_time", label: "Start Time", minWidth: 170, enableSort: true },
    { id: "end_time", label: "End Time", minWidth: 170, enableSort: true },
    { id: "status", label: "Status", minWidth: 150 },
    { id: "action", label: "Action", minWidth: 100 },
  ];

  const rows = contracts.map((contract) => ({
    ...contract,
    arbitrage_percent:
      contract.arbitrage_percent !== undefined
        ? `${Number(contract.arbitrage_percent).toFixed(2)}%`
        : "-",
    discrepancy_bps:
      contract.discrepancy_bps !== undefined
        ? Number(contract.discrepancy_bps)
        : "-",
    min_volume: formatCurrency(contract.min_volume),
    max_volume: formatCurrency(contract.max_volume),
    batch_volume: formatCurrency(contract.batch_volume),
    start_time: formatDateTime(contract.start_time),
    end_time: formatDateTime(contract.end_time),
    status: (
      <select
        className="form-control"
        value={contract.status || "ACTIVE"}
        onChange={(event) => updateContractStatus(contract, event.target.value)}
      >
        <option value="ACTIVE">ACTIVE</option>
        <option value="INACTIVE">INACTIVE</option>
      </select>
    ),
    action: (
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          size="small"
          color="warning"
          onClick={() => openEditDialog(contract)}
        >
          Edit
        </Button>
      </Box>
    ),
  }));

  const overrideRequired = Boolean(editingContract);

  return (
    <Box sx={{ px: 6 }} className="main-box">
      <Box
        sx={{
          py: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h4" component="h4" sx={{ color: "#33343a" }}>
            Offramp Rate Contracts
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {(loading || partnerLoading) && <CircularProgress size={26} />}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Contract
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 3,
        }}
      >
        <TextField
          label="Search"
          variant="standard"
          onKeyUp={(event) => setSearchKey(event.target.value)}
          InputProps={{
            endAdornment: <SearchIcon sx={{ color: "#8a8a8a" }} />,
          }}
        />
      </Box>

      {error && !loading ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <CustomTable data={filterList(rows, searchKey)} columns={columns} />

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(handleSaveContract)}>
          <DialogTitle>
            {editingContract ? "Edit Rate Contract" : "Add Rate Contract"}
          </DialogTitle>
          <DialogContent dividers>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="partner"
                  >
                    Partner: <small className="text-danger fs-6">*</small>
                  </label>
                  <Controller
                    control={control}
                    name="partner"
                    rules={{ required: "Partner is required" }}
                    render={({ field }) => (
                      <Select
                        inputId="partner"
                        isLoading={partnerLoading}
                        options={partnerOptions}
                        value={selectedPartnerOption(field.value)}
                        onChange={(option) =>
                          field.onChange(option?.value || "")
                        }
                        placeholder="Select partner"
                      />
                    )}
                  />
                  <FieldError error={errors.partner} />
                </div>
              </div>
              {editingContract ? (
                <div className="col-md-6">
                  <div className="form-group">
                    <label
                      className="form-label fw-bold text-secondary user-select-none"
                      htmlFor="status"
                    >
                      Status: <small className="text-danger fs-6">*</small>
                    </label>
                    <Controller
                      control={control}
                      name="status"
                      rules={{ required: "Status is required" }}
                      render={({ field }) => (
                        <Select
                          inputId="status"
                          options={STATUS_OPTIONS}
                          value={selectedStatusOption(field.value)}
                          onChange={(option) =>
                            field.onChange(option?.value || "")
                          }
                          placeholder="Select status"
                        />
                      )}
                    />
                    <FieldError error={errors.status} />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="start_time"
                  >
                    Start Time: <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("start_time", {
                      required: "Start time is required",
                    })}
                    className={`form-control ${
                      errors.start_time && "invalid-form-input"
                    }`}
                    type="datetime-local"
                    id="start_time"
                  />
                  <FieldError error={errors.start_time} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="end_time"
                  >
                    End Time: <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("end_time", {
                      required: "End time is required",
                    })}
                    className={`form-control ${
                      errors.end_time && "invalid-form-input"
                    }`}
                    type="datetime-local"
                    id="end_time"
                  />
                  <FieldError error={errors.end_time} />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="arbitrage_percent"
                  >
                    Arbitrage (%): <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("arbitrage_percent", {
                      required: "Arbitrage percent is required",
                      min: {
                        value: 0,
                        message: "Arbitrage percent cannot be negative",
                      },
                      max: {
                        value: MAX_ARBITRAGE_PERCENT,
                        message: `Arbitrage percent cannot exceed ${MAX_ARBITRAGE_PERCENT}`,
                      },
                      pattern: {
                        value: /^\d+(\.\d{1,2})?$/,
                        message: "Use up to 2 decimal places",
                      },
                    })}
                    className={`form-control ${
                      errors.arbitrage_percent && "invalid-form-input"
                    }`}
                    type="number"
                    step="0.01"
                    min="0"
                    max={MAX_ARBITRAGE_PERCENT}
                    id="arbitrage_percent"
                  />
                  <FieldError error={errors.arbitrage_percent} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="discrepancy_bps"
                  >
                    Acceptable Discrepancy (BPS):{" "}
                    <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("discrepancy_bps", {
                      required: "Discrepancy BPS is required",
                      min: {
                        value: 0,
                        message: "Discrepancy BPS cannot be negative",
                      },
                      pattern: {
                        value: /^\d+$/,
                        message: "Discrepancy BPS must be an integer",
                      },
                    })}
                    className={`form-control ${
                      errors.discrepancy_bps && "invalid-form-input"
                    }`}
                    type="number"
                    min="0"
                    step="1"
                    id="discrepancy_bps"
                  />
                  <FieldError error={errors.discrepancy_bps} />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="min_volume"
                  >
                    Min Volume (USD):{" "}
                    <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("min_volume", {
                      required: "Min volume is required",
                      min: {
                        value: 0,
                        message: "Min volume cannot be negative",
                      },
                    })}
                    className={`form-control ${
                      errors.min_volume && "invalid-form-input"
                    }`}
                    type="number"
                    min="0"
                    step="0.01"
                    id="min_volume"
                  />
                  <FieldError error={errors.min_volume} />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="max_volume"
                  >
                    Max Volume (USD):{" "}
                    <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("max_volume", {
                      required: "Max volume is required",
                      min: {
                        value: 0,
                        message: "Max volume cannot be negative",
                      },
                    })}
                    className={`form-control ${
                      errors.max_volume && "invalid-form-input"
                    }`}
                    type="number"
                    min="0"
                    step="0.01"
                    id="max_volume"
                  />
                  <FieldError error={errors.max_volume} />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label
                    className="form-label fw-bold text-secondary user-select-none"
                    htmlFor="batch_volume"
                  >
                    Batched Volume (USD):{" "}
                    <small className="text-danger fs-6">*</small>
                  </label>
                  <input
                    {...register("batch_volume", {
                      required: "Batched volume is required",
                      min: {
                        value: 0,
                        message: "Batched volume cannot be negative",
                      },
                    })}
                    className={`form-control ${
                      errors.batch_volume && "invalid-form-input"
                    }`}
                    type="number"
                    min="0"
                    step="0.01"
                    id="batch_volume"
                  />
                  <FieldError error={errors.batch_volume} />
                </div>
              </div>
            </div>

            {editingContract ? (
              <div className="row">
                <div className="col-md-12">
                  <div className="form-group">
                    <label
                      className="form-label fw-bold text-secondary user-select-none"
                      htmlFor="override_reason"
                    >
                      Override Reason:
                      {overrideRequired ? (
                        <small className="text-danger fs-6"> *</small>
                      ) : null}
                    </label>
                    <textarea
                      {...register("override_reason")}
                      className={`form-control ${
                        errors.override_reason && "invalid-form-input"
                      }`}
                      id="override_reason"
                      rows={3}
                    ></textarea>
                    <FieldError error={errors.override_reason} />
                  </div>
                </div>
              </div>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={submitLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitLoading}>
              {submitLoading ? <CircularProgress size={22} /> : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default OfframpRateContracts;
